import { useState, useCallback } from "react";
import { ChevronIcon } from "@/components/common/Icons";

/**
 * useAccordion Hook
 * Manages accordion state with optional single/multi mode
 */
export function useAccordion(allowMultiple = false) {
  const [activeItems, setActiveItems] = useState(new Set());

  const toggle = useCallback(
    (id) => {
      setActiveItems((prev) => {
        const next = new Set(allowMultiple ? prev : []);
        if (prev.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [allowMultiple],
  );

  const isActive = useCallback((id) => activeItems.has(id), [activeItems]);

  return { toggle, isActive };
}

/**
 * AccordionItem Component
 * Individual accordion item with question/answer
 */
export function AccordionItem({
  id,
  question,
  answer,
  isActive,
  onToggle,
  variant = "default", // 'default' | 'footer'
}) {
  const isFooter = variant === "footer";

  const containerStyles = isFooter
    ? "border-b border-white/5"
    : `bg-white border border-transparent rounded-[1.2rem] overflow-hidden transition-all
       hover:-translate-y-px`;

  const questionStyles = isFooter
    ? "w-full flex items-center justify-between gap-4 py-4 cursor-pointer"
    : "w-full flex items-center justify-between gap-6 py-5 px-6 cursor-pointer font-medium";

  const questionTextStyles = isFooter
    ? "flex-1 text-base font-[480] leading-relaxed text-left text-white"
    : "flex-1 text-[1.02rem] leading-relaxed text-left";

  const answerStyles = isFooter
    ? `overflow-hidden transition-all duration-300 ${isActive ? "max-h-80" : "max-h-0"}`
    : `overflow-hidden px-6 transition-all duration-400 -translate-y-1
       ${isActive ? "max-h-[540px] translate-y-0" : "max-h-0"}`;

  const iconStyles = isFooter
    ? "w-[18px] h-[18px] opacity-60 transition-transform"
    : "w-[22px] h-[18px] transition-transform";

  return (
    <div className={containerStyles}>
      <button
        onClick={() => onToggle(id)}
        className={questionStyles}
        aria-expanded={isActive}
      >
        <span
          className={`${questionTextStyles} ${isActive && !isFooter ? "text-green" : ""}`}
        >
          {question}
        </span>
        <span className={`${iconStyles} ${isActive ? "rotate-180" : ""}`}>
          <ChevronIcon
            className="w-full h-full"
            color={
              isFooter
                ? "white"
                : isActive
                  ? "var(--color-green)"
                  : "var(--color-main)"
            }
          />
        </span>
      </button>
      <div className={answerStyles}>
        {isFooter ? (
          <ul className="flex flex-col gap-2 pb-5 list-none">
            {answer.map((link, index) => (
              <li key={index}>
                <a
                  href={link.href}
                  target={link.target}
                  rel={link.rel}
                  className="text-white/70 text-base leading-relaxed hover:text-white transition-opacity"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <div
            className="[&_p]:text-gray [&_p]:leading-[1.6] [&_p]:pb-6 [&_p]:transition-colors [&_p]:duration-300 [&_p]:ease-out [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0 [&_ul]:mb-6 [&_li]:text-gray [&_li]:leading-[1.6] [&_li]:pl-[1.3rem] [&_li]:pb-[0.45rem] [&_li]:relative [&_li::before]:content-['•']
  [&_li::before]:absolute
  [&_li::before]:left-0
  [&_li::before]:font-bold
  [&_li::before]:text-[1.6em]
  [&_li::before]:-mt-[0.6rem]
  [&_li::before]:opacity-30
  [&_a]:text-green [&_a]:no-underline [&_a]:transition-opacity [&_a]:duration-300 [&_a]:ease-out
  [&_img]:max-h-[460px] [&_img]:w-auto [&_img]:h-auto [&_img]:block [&_img]:mx-auto [&_img]:mb-6"
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Accordion Component
 * Container for accordion items
 */
export function Accordion({
  items,
  allowMultiple = false,
  variant = "default",
  className = "",
}) {
  const { toggle, isActive } = useAccordion(allowMultiple);

  const containerStyles =
    variant === "footer"
      ? className
      : `max-w-[780px] mx-auto flex flex-col gap-2.5 ${className}`;

  return (
    <div className={containerStyles}>
      {items.map((item) => (
        <AccordionItem
          key={item.id}
          id={item.id}
          question={item.question}
          answer={item.answer}
          isActive={isActive(item.id)}
          onToggle={toggle}
          variant={variant}
        />
      ))}
    </div>
  );
}

export default Accordion;
